import { FunctionComponent } from "react";

import { Modal } from "@/components";

import { formatStopDate } from "./CampaignColumns";

import type { StopDateOverwrite } from "../ListEditCopywriting.helpers";

export interface ApplyEventStopDateModalProps {
	isOpened: boolean;
	/** Campaigns whose existing stop date will be overwritten. */
	overwrites: StopDateOverwrite[];
	/** The event end date that becomes every campaign's stop date. */
	endDate: string;
	onConfirm: () => void;
	onClose: () => void;
}

/**
 * Confirms overwriting campaigns that already have a stop date when the user clicks "Apply event
 * end date" (PREC-3540). Lists each affected campaign with its current → new date so the overwrite
 * is explicit; confirming overwrites EVERY campaign's stop date with the event end date.
 */
export const ApplyEventStopDateModal: FunctionComponent<ApplyEventStopDateModalProps> = ({
	isOpened,
	overwrites,
	endDate,
	onConfirm,
	onClose,
}) => {
	if (!isOpened) {
		return null;
	}

	const isPlural = overwrites.length !== 1;

	return (
		<Modal
			handleClose={onClose}
			handleSave={onConfirm}
			isOpened={isOpened}
			submitButtonText="Overwrite stop dates"
			title="Apply event end date?"
		>
			<div className="text-sm text-gray-700">
				<p className="mb-3">
					This sets <span className="font-semibold">{formatStopDate(endDate)}</span> as the stop
					date for every campaign on this list. The following campaign{isPlural ? "s" : ""} already
					{isPlural ? " have" : " has"} a different stop date that will be overwritten:
				</p>
				<ul className="flex flex-col gap-2">
					{overwrites.map((campaign) => (
						<li
							key={campaign.uuid}
							className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2"
						>
							<span className="truncate font-medium text-gray-900">{campaign.name}</span>
							<span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-text-meta">
								<span className="line-through">
									{formatStopDate(campaign.currentStopCampaignAt)}
								</span>
								<span aria-hidden>→</span>
								<span className="font-semibold text-gray-900">{formatStopDate(endDate)}</span>
							</span>
						</li>
					))}
				</ul>
			</div>
		</Modal>
	);
};
